import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import {
  CollapseSection,
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  LabeledValue,
  ListHeader,
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalOpenButton,
  ModalTitle,
  ModalWithSidePanelDialog,
  ModalWithSidePanelSections,
} from "@msys/ui";
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { SelectedBoxWithIndicator } from "../../../commons/SelectedBoxWithIndicator.js";
import { CategoriesInput } from "../../categories/CategoriesInput.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { UserAvatar } from "../../users/UserAvatar.js";
import { useOrganisationRoles } from "../../users/useRoles.js";
import {
  OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment,
  useAvailableTriggerEventNamesQuery,
  useCreateOrganisationWorkflowMutation,
  useModifyOrganisationWorkflowSettingsMutation,
  useWorkflowTypeDefinitionsQuery,
  WorkflowTypeDefinitionsQuery,
} from "../boxes/OrganisationAutomatedWorkflowsBox.generated.js";
import { useAddOrganisationEmailTemplateMutation } from "../boxes/OrganisationEmailSettingsBox.generated.js";
import { useEmailTemplateContexts } from "../boxes/useEmailTemplateContexts.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
} from "../OrganisationMemberSelect.generated.js";
import { useTemplateQuoteSelectFieldQuery } from "../../templates/quote/TemplateQuoteSelectField.generated.js";
import { OrganisationEmailTemplateModal } from "./OrganisationEmailTemplateModal.js";

type ConfigField =
  WorkflowTypeDefinitionsQuery["workflowTypeDefinitions"][number]["configFields"][number];

const SOURCE_TYPES = [
  "Manual",
  "FlowWizard",
  "Flow3D",
  "FlowVoiceAssistant",
  "FlowM1",
  "RequestManual",
  "RequestAutomated",
  "Import",
  "QuoteProductsDistribution",
  "ContracteeInvitation",
];

type SidePanelState =
  | { status: "closed" }
  | { status: "email-template-select"; configKey: string }
  | { status: "email-template-create"; configKey: string }
  | { status: "source-type-select" }
  | { status: "quote-template-select" }
  | { status: "category-select" }
  | { status: "type-of-project-select" }
  | { status: "recipient-select"; configKey: string }
  | { status: "trigger-event-select" };

interface FormValues {
  workflowType: string;
  description: string;
  triggerEventNames: string[];
  sourceTypeFilter: string[];
  quoteTemplateFilter: string[];
  categoryFilter: string[];
  typeOfProjectFilter: string[];
  config: Record<string, unknown>;
}

interface Props {
  workflow?: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment;
  workflows?: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment[];
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  organisationId?: string;
  handleClose: () => void;
}

export const OrganisationWorkflowPanel = ({
  workflow,
  workflows = [],
  emailTemplates,
  organisationId,
  handleClose,
}: Props) => {
  const isEditMode = !!workflow;
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();
  const formId = React.useMemo(() => uniqueId(), []);

  const [sidePanelState, setSidePanelState] = React.useState<SidePanelState>({
    status: "closed",
  });
  const showSideContent = sidePanelState.status !== "closed";

  const handleCloseSidePanel = React.useCallback(() => {
    setSidePanelState({ status: "closed" });
  }, []);

  // Queries
  const { data: typeDefsData } = useWorkflowTypeDefinitionsQuery({ client });
  const { data: triggerEventNamesData } = useAvailableTriggerEventNamesQuery({
    client,
  });

  const workflowTypeDefs = typeDefsData?.workflowTypeDefinitions ?? [];

  // Mutations
  const [createWorkflow] = useCreateOrganisationWorkflowMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationWorkflows,
      namedOperations.Query.OrganisationSettingsIntegrations,
    ],
  });

  const [modifyWorkflowSettings] =
    useModifyOrganisationWorkflowSettingsMutation({ client });

  const [addEmailTemplate] = useAddOrganisationEmailTemplateMutation({
    client,
    refetchQueries: [namedOperations.Query.OrganisationSettingsIntegrations],
  });

  // Options
  const workflowTypeOptions = React.useMemo(
    () =>
      workflowTypeDefs.map(def => ({
        value: def.workflowType,
        label: t(def.label as any, { ns: "OrganisationSettings" }),
      })),
    [t, workflowTypeDefs]
  );

  const getTypeDef = React.useCallback(
    (workflowType: string) =>
      workflowTypeDefs.find(d => d.workflowType === workflowType),
    [workflowTypeDefs]
  );

  const getConflictingWorkflow = React.useCallback(
    (values: FormValues) => {
      const typeDef = workflowTypeDefs.find(
        d => d.workflowType === values.workflowType
      );
      const policy = typeDef?.instancePolicy as
        | { type: string; fields?: { source: string; key: string }[] }
        | undefined;

      if (!policy || policy.type === "unrestricted") return undefined;

      const activeOthers = workflows.filter(
        w =>
          w.workflowType === values.workflowType &&
          w.active &&
          w.id !== workflow?.id
      );

      if (policy.type === "singleton") return activeOthers[0];

      if (policy.type === "exclusive" && policy.fields) {
        const candidateTriggerFilter =
          values.sourceTypeFilter.length > 0
            ? { "payload.sourceType": values.sourceTypeFilter }
            : {};

        for (const existing of activeOthers) {
          const existingTriggerFilter =
            (existing.triggerFilter as Record<string, unknown>) ?? {};
          const existingConfig =
            (existing.config as Record<string, unknown>) ?? {};

          const allOverlap = policy.fields.every(field => {
            if (field.source === "triggerFilter") {
              const a = candidateTriggerFilter[
                field.key as keyof typeof candidateTriggerFilter
              ] as string[] | undefined;
              const b = existingTriggerFilter[field.key] as
                | string[]
                | undefined;
              const arrA = Array.isArray(a) ? a : [];
              const arrB = Array.isArray(b) ? b : [];
              if (arrA.length === 0 || arrB.length === 0) return true;
              const setB = new Set(arrB);
              return arrA.some(v => setB.has(v));
            }
            // config
            const a = values.config[field.key];
            const b = existingConfig[field.key];
            if (a == null || b == null) return false;
            return a === b;
          });

          if (allOverlap) return existing;
        }
      }

      return undefined;
    },
    [workflowTypeDefs, workflows, workflow]
  );

  const initialValues: FormValues = React.useMemo(() => {
    if (isEditMode) {
      const config = (workflow.config as Record<string, unknown>) ?? {};
      const triggerFilter = workflow.triggerFilter as Record<
        string,
        string[]
      > | null;
      return {
        workflowType: workflow.workflowType,
        description: workflow.description,
        triggerEventNames: workflow.triggerEventNames ?? [],
        sourceTypeFilter: triggerFilter?.["payload.sourceType"] ?? [],
        quoteTemplateFilter: triggerFilter?.["payload.templateId"] ?? [],
        categoryFilter: triggerFilter?.["payload.category"] ?? [],
        typeOfProjectFilter: triggerFilter?.["payload.typeOfProject"] ?? [],
        config,
      };
    }
    return {
      workflowType: "",
      description: "",
      triggerEventNames: [],
      sourceTypeFilter: [],
      quoteTemplateFilter: [],
      categoryFilter: [],
      typeOfProjectFilter: [],
      config: {},
    };
  }, [isEditMode, workflow]);

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      try {
        const triggerFilter = {
          ...(values.sourceTypeFilter.length > 0
            ? { "payload.sourceType": values.sourceTypeFilter }
            : {}),
          ...(values.quoteTemplateFilter.length > 0
            ? { "payload.templateId": values.quoteTemplateFilter }
            : {}),
          ...(values.categoryFilter.length > 0
            ? { "payload.category": values.categoryFilter }
            : {}),
          ...(values.typeOfProjectFilter.length > 0
            ? { "payload.typeOfProject": values.typeOfProjectFilter }
            : {}),
        };

        if (isEditMode) {
          await modifyWorkflowSettings({
            variables: {
              workflowId: workflow.id,
              input: {
                description: values.description,
                triggerEventNames: values.triggerEventNames,
                triggerFilter,
                config: values.config,
              },
            },
          });
        } else {
          await createWorkflow({
            variables: {
              input: {
                workflowType: values.workflowType,
                description: values.description,
                triggerEventNames: values.triggerEventNames,
                triggerFilter,
                config: values.config,
              },
            },
          });
        }
        handleClose();
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [
      isEditMode,
      workflow,
      createWorkflow,
      modifyWorkflowSettings,
      handleClose,
      enqueueSnackbar,
    ]
  );

  return (
    <ModalWithSidePanelDialog
      fixedHeight={showSideContent}
      dialogProps={{
        maxWidth: showSideContent ? "lg" : "sm",
      }}
      handleClose={handleClose}
    >
      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {formikProps => {
          const typeDef = getTypeDef(formikProps.values.workflowType);
          const configFields = typeDef?.configFields ?? [];
          const allRequiredConfigured =
            configFields
              .filter(f => f.required)
              .every(f => !!formikProps.values.config[f.key]) &&
            (formikProps.values.config.assignTo !== "specificUser" ||
              !!formikProps.values.config.recipientUserId);
          const conflictingWorkflow = getConflictingWorkflow(
            formikProps.values
          );

          return (
            <ModalWithSidePanelSections
              sideContent={
                sidePanelState.status === "email-template-select" ||
                sidePanelState.status === "email-template-create" ? (
                  <EmailTemplateSidePanel
                    emailTemplates={emailTemplates}
                    handleClose={handleCloseSidePanel}
                    addEmailTemplate={addEmailTemplate}
                    configKey={sidePanelState.configKey}
                  />
                ) : sidePanelState.status === "source-type-select" ? (
                  <SourceTypeSelectPanel handleClose={handleCloseSidePanel} />
                ) : sidePanelState.status === "quote-template-select" ? (
                  <QuoteTemplateFilterPanel
                    handleClose={handleCloseSidePanel}
                  />
                ) : sidePanelState.status === "category-select" ? (
                  <CategoryFilterPanel handleClose={handleCloseSidePanel} />
                ) : sidePanelState.status === "type-of-project-select" ? (
                  <TypeOfProjectFilterPanel
                    handleClose={handleCloseSidePanel}
                  />
                ) : sidePanelState.status === "recipient-select" ? (
                  <RecipientSelectSidePanel
                    handleClose={handleCloseSidePanel}
                    configKey={sidePanelState.configKey}
                  />
                ) : sidePanelState.status === "trigger-event-select" ? (
                  <TriggerEventSelectPanel
                    handleClose={handleCloseSidePanel}
                    availableEvents={
                      triggerEventNamesData?.availableTriggerEventNames ?? []
                    }
                    compatibleEvents={typeDef?.compatibleTriggerEvents ?? []}
                  />
                ) : sidePanelState.status === "closed" ? null : (
                  assertNever(sidePanelState)
                )
              }
              maxWidth="sm"
              maxWidthWithSideContent="lg"
            >
              <ModalTitle
                title={
                  isEditMode
                    ? t("Edit workflow" as any, { ns: "OrganisationSettings" })
                    : t("Create workflow" as any, {
                        ns: "OrganisationSettings",
                      })
                }
                handleClose={handleClose}
              />
              <ModalContent>
                <Form id={formId}>
                  <Stack spacing={2}>
                    {!isEditMode && (
                      <WorkflowTypeWatcher
                        workflowTypeDefs={workflowTypeDefs}
                      />
                    )}
                    <SourceTypeCreatorWatcher configFields={configFields} />

                    {/* Workflow type */}
                    {isEditMode ? (
                      <LabeledValue
                        label={t("Workflow type" as any, {
                          ns: "OrganisationSettings",
                        })}
                      >
                        {t((typeDef?.label ?? workflow.workflowType) as any, {
                          ns: "OrganisationSettings",
                        })}
                      </LabeledValue>
                    ) : (
                      <>
                        <SelectField
                          name="workflowType"
                          label={t("Workflow type" as any, {
                            ns: "OrganisationSettings",
                          })}
                          options={workflowTypeOptions}
                        />
                        {typeDef && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: -1 }}
                          >
                            {t(typeDef.description as any, {
                              ns: "OrganisationSettings",
                            })}
                          </Typography>
                        )}
                      </>
                    )}

                    {/* Show remaining fields only after type selection */}
                    {(isEditMode || formikProps.values.workflowType) && (
                      <>
                        {/* Description */}
                        <TextField
                          name="description"
                          label={t("Description", {
                            ns: "OrganisationSettings",
                          })}
                          required={!isEditMode}
                        />

                        {/* --- Trigger Section --- */}
                        <CollapseSection
                          title={t("Trigger" as any, {
                            ns: "OrganisationSettings",
                          })}
                          isInitiallyExpanded
                        >
                          <Stack spacing={0.5}>
                            <Box width="100%">
                              {formikProps.values.triggerEventNames.length >
                              0 ? (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <LabeledValue
                                    label={t("Trigger events" as any, {
                                      ns: "OrganisationSettings",
                                    })}
                                  >
                                    {formikProps.values.triggerEventNames.join(
                                      ", "
                                    )}
                                  </LabeledValue>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() =>
                                      setSidePanelState({
                                        status: "trigger-event-select",
                                      })
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Stack>
                              ) : (
                                <SelectedBoxWithIndicator
                                  selected={
                                    sidePanelState.status ===
                                    "trigger-event-select"
                                  }
                                >
                                  <Button
                                    size="small"
                                    color="secondary"
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    sx={{ textWrap: "nowrap" }}
                                    onClick={() =>
                                      setSidePanelState({
                                        status: "trigger-event-select",
                                      })
                                    }
                                  >
                                    {t("Select trigger events" as any, {
                                      ns: "OrganisationSettings",
                                    })}
                                  </Button>
                                </SelectedBoxWithIndicator>
                              )}
                            </Box>
                          </Stack>
                        </CollapseSection>

                        {/* Collapsible Trigger Filters */}
                        <CollapseSection
                          title={t("Filters" as any, {
                            ns: "OrganisationSettings",
                          })}
                          isInitiallyExpanded={false}
                        >
                          <Stack spacing={2}>
                            {/* Source type filter */}
                            <Stack spacing={0.5}>
                              <Box width="100%">
                                {formikProps.values.sourceTypeFilter.length >
                                0 ? (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <LabeledValue
                                      label={t("Source type filter" as any, {
                                        ns: "OrganisationSettings",
                                      })}
                                    >
                                      {formikProps.values.sourceTypeFilter.join(
                                        ", "
                                      )}
                                    </LabeledValue>
                                    <IconButton
                                      color="primary"
                                      size="small"
                                      onClick={() =>
                                        setSidePanelState({
                                          status: "source-type-select",
                                        })
                                      }
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <SelectedBoxWithIndicator
                                    selected={
                                      sidePanelState.status ===
                                      "source-type-select"
                                    }
                                  >
                                    <Button
                                      size="small"
                                      color="secondary"
                                      variant="text"
                                      startIcon={<AddIcon />}
                                      sx={{ textWrap: "nowrap" }}
                                      onClick={() =>
                                        setSidePanelState({
                                          status: "source-type-select",
                                        })
                                      }
                                    >
                                      {t("Select source types" as any, {
                                        ns: "OrganisationSettings",
                                      })}
                                    </Button>
                                  </SelectedBoxWithIndicator>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ pl: 0.5 }}
                              >
                                {t(
                                  "Filter which quote sources trigger this workflow. Leave empty for all sources." as any,
                                  { ns: "OrganisationSettings" }
                                )}
                              </Typography>
                            </Stack>

                            {/* Quote template filter — only for QUOTE_CREATED */}
                            {formikProps.values.triggerEventNames.includes(
                              "QUOTE_CREATED"
                            ) && (
                              <Stack spacing={0.5}>
                                <Box width="100%">
                                  {formikProps.values.quoteTemplateFilter
                                    .length > 0 ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                    >
                                      <LabeledValue
                                        label={t(
                                          "Quote template filter" as any,
                                          {
                                            ns: "OrganisationSettings",
                                          }
                                        )}
                                      >
                                        {
                                          formikProps.values.quoteTemplateFilter
                                            .length
                                        }{" "}
                                        {t("selected" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </LabeledValue>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "quote-template-select",
                                          })
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    <SelectedBoxWithIndicator
                                      selected={
                                        sidePanelState.status ===
                                        "quote-template-select"
                                      }
                                    >
                                      <Button
                                        size="small"
                                        color="secondary"
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        sx={{ textWrap: "nowrap" }}
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "quote-template-select",
                                          })
                                        }
                                      >
                                        {t("Select quote templates" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </Button>
                                    </SelectedBoxWithIndicator>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ pl: 0.5 }}
                                >
                                  {t(
                                    "Filter which quote templates trigger this workflow. Leave empty for all templates." as any,
                                    { ns: "OrganisationSettings" }
                                  )}
                                </Typography>
                              </Stack>
                            )}

                            {/* Category filter — only for QUOTE_CREATED */}
                            {formikProps.values.triggerEventNames.includes(
                              "QUOTE_CREATED"
                            ) && (
                              <Stack spacing={0.5}>
                                <Box width="100%">
                                  {formikProps.values.categoryFilter.length >
                                  0 ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                    >
                                      <LabeledValue
                                        label={t("Category filter" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      >
                                        {
                                          formikProps.values.categoryFilter
                                            .length
                                        }{" "}
                                        {t("selected" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </LabeledValue>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "category-select",
                                          })
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    <SelectedBoxWithIndicator
                                      selected={
                                        sidePanelState.status ===
                                        "category-select"
                                      }
                                    >
                                      <Button
                                        size="small"
                                        color="secondary"
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        sx={{ textWrap: "nowrap" }}
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "category-select",
                                          })
                                        }
                                      >
                                        {t("Select categories" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </Button>
                                    </SelectedBoxWithIndicator>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ pl: 0.5 }}
                                >
                                  {t(
                                    "Filter which categories trigger this workflow. Leave empty for all categories." as any,
                                    { ns: "OrganisationSettings" }
                                  )}
                                </Typography>
                              </Stack>
                            )}

                            {/* Type of project filter — only for QUOTE_CREATED */}
                            {formikProps.values.triggerEventNames.includes(
                              "QUOTE_CREATED"
                            ) && (
                              <Stack spacing={0.5}>
                                <Box width="100%">
                                  {formikProps.values.typeOfProjectFilter
                                    .length > 0 ? (
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                    >
                                      <LabeledValue
                                        label={t(
                                          "Type of project filter" as any,
                                          {
                                            ns: "OrganisationSettings",
                                          }
                                        )}
                                      >
                                        {
                                          formikProps.values.typeOfProjectFilter
                                            .length
                                        }{" "}
                                        {t("selected" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </LabeledValue>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "type-of-project-select",
                                          })
                                        }
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Stack>
                                  ) : (
                                    <SelectedBoxWithIndicator
                                      selected={
                                        sidePanelState.status ===
                                        "type-of-project-select"
                                      }
                                    >
                                      <Button
                                        size="small"
                                        color="secondary"
                                        variant="text"
                                        startIcon={<AddIcon />}
                                        sx={{ textWrap: "nowrap" }}
                                        onClick={() =>
                                          setSidePanelState({
                                            status: "type-of-project-select",
                                          })
                                        }
                                      >
                                        {t("Select project types" as any, {
                                          ns: "OrganisationSettings",
                                        })}
                                      </Button>
                                    </SelectedBoxWithIndicator>
                                  )}
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ pl: 0.5 }}
                                >
                                  {t(
                                    "Filter which project types trigger this workflow. Leave empty for all project types." as any,
                                    { ns: "OrganisationSettings" }
                                  )}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </CollapseSection>

                        {/* Instance policy conflict alert */}
                        {conflictingWorkflow && (
                          <Alert severity="error">
                            {`${t("This workflow conflicts with" as any, { ns: "OrganisationSettings" })} "${conflictingWorkflow.description}". ${t("Adjust the source type filter to avoid overlap." as any, { ns: "OrganisationSettings" })}`}
                          </Alert>
                        )}

                        {/* --- Response Section --- */}
                        {configFields.length > 0 && (
                          <CollapseSection
                            title={t("Response" as any, {
                              ns: "OrganisationSettings",
                            })}
                            isInitiallyExpanded
                          >
                            <Stack spacing={2}>
                              {configFields.map(field => (
                                <DynamicConfigField
                                  key={field.key}
                                  field={field}
                                  emailTemplates={emailTemplates}
                                  sidePanelState={sidePanelState}
                                  setSidePanelState={setSidePanelState}
                                />
                              ))}
                            </Stack>
                          </CollapseSection>
                        )}
                      </>
                    )}
                  </Stack>
                </Form>
              </ModalContent>
              <ModalActions
                actionButtons={[
                  {
                    label: t("Cancel", { ns: "Global" }),
                    handleClick: handleClose,
                    buttonProps: {
                      variant: "text",
                      disabled: formikProps.isSubmitting,
                    },
                  },
                  {
                    label: isEditMode
                      ? t("Save", { ns: "Global" })
                      : t("Create", { ns: "Global" }),
                    buttonProps: {
                      type: "submit",
                      form: formId,
                      disabled:
                        !allRequiredConfigured ||
                        !!conflictingWorkflow ||
                        formikProps.values.triggerEventNames.length === 0 ||
                        (!isEditMode &&
                          (!formikProps.values.workflowType ||
                            !formikProps.values.description)),
                      loading: formikProps.isSubmitting,
                    },
                  },
                ]}
              />
            </ModalWithSidePanelSections>
          );
        }}
      </Formik>
    </ModalWithSidePanelDialog>
  );
};

// --- Dynamic Config Field ---

function DynamicConfigField({
  field,
  emailTemplates,
  sidePanelState,
  setSidePanelState,
}: {
  field: ConfigField;
  emailTemplates: Props["emailTemplates"];
  sidePanelState: SidePanelState;
  setSidePanelState: (state: SidePanelState) => void;
}) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const configValue = values.config[field.key];

  switch (field.fieldType) {
    case "emailTemplatePicker": {
      const selectedId = configValue as string | null;
      const selectedTemplate = selectedId
        ? emailTemplates.find(et => et.id === selectedId)
        : null;
      const isOrphanedTemplate = !!selectedId && !selectedTemplate;
      return (
        <Box width="100%">
          {selectedId ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabeledValue
                label={t(field.label as any, { ns: "OrganisationSettings" })}
              >
                {isOrphanedTemplate ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                    <Typography variant="body2" color="warning.main">
                      {t("Template no longer exists" as any, {
                        ns: "OrganisationSettings",
                      })}
                    </Typography>
                  </Stack>
                ) : (
                  selectedTemplate?.description ||
                  t("No description" as any, { ns: "Global" })
                )}
              </LabeledValue>
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  setFieldValue("config", {
                    ...values.config,
                    [field.key]: null,
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <SelectedBoxWithIndicator
              selected={
                (sidePanelState.status === "email-template-select" ||
                  sidePanelState.status === "email-template-create") &&
                sidePanelState.configKey === field.key
              }
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "email-template-select",
                    configKey: field.key,
                  })
                }
              >
                {t("Select email template" as any, {
                  ns: "OrganisationSettings",
                })}
              </Button>
            </SelectedBoxWithIndicator>
          )}
        </Box>
      );
    }
    case "assignmentStrategyPicker": {
      const assignTo = (configValue as string) ?? "specificUser";
      const creatorCapableSourceTypes = ((
        field.metadata as Record<string, unknown> | null
      )?.creatorCapableSourceTypes ?? []) as string[];

      const selectedSourceTypes = values.sourceTypeFilter;
      const hasSourceTypes = selectedSourceTypes.length > 0;
      const allCreatorCapable =
        hasSourceTypes &&
        selectedSourceTypes.every(st => creatorCapableSourceTypes.includes(st));
      const creatorEnabled = allCreatorCapable;

      const selectedNonCreatorSources = selectedSourceTypes.filter(
        st => !creatorCapableSourceTypes.includes(st)
      );

      return (
        <FormControl component="fieldset">
          <FormLabel component="legend">
            {t(field.label as any, { ns: "OrganisationSettings" })}
          </FormLabel>
          <RadioGroup
            value={assignTo}
            onChange={e => {
              setFieldValue("config", {
                ...values.config,
                [field.key]: e.target.value,
              });
            }}
          >
            <FormControlLabel
              value="specificUser"
              control={<Radio size="small" />}
              label={t("Specific user" as any, {
                ns: "OrganisationSettings",
              })}
            />
            <FormControlLabel
              value="creator"
              control={<Radio size="small" />}
              label={t("Opportunity creator" as any, {
                ns: "OrganisationSettings",
              })}
              disabled={!creatorEnabled}
            />
          </RadioGroup>
          {!creatorEnabled && selectedNonCreatorSources.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t("Source types without a creator" as any, {
                ns: "OrganisationSettings",
              })}
              {": "}
              {selectedNonCreatorSources.join(", ")}
            </Typography>
          )}
          {!creatorEnabled && !hasSourceTypes && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {t(
                "Select one or more creator-capable source types to enable this option." as any,
                { ns: "OrganisationSettings" }
              )}
            </Typography>
          )}
        </FormControl>
      );
    }
    case "memberPicker": {
      const selectedId = configValue as string | null;
      // Hide member picker when assignTo is "creator"
      if (values.config.assignTo === "creator") return null;
      return (
        <Box width="100%">
          {selectedId ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <LabeledValue
                label={t(field.label as any, { ns: "OrganisationSettings" })}
              >
                <MemberButtonLabel memberId={selectedId} />
              </LabeledValue>
              <IconButton
                color="primary"
                size="small"
                onClick={() => {
                  setFieldValue("config", {
                    ...values.config,
                    [field.key]: null,
                  });
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ) : (
            <SelectedBoxWithIndicator
              selected={
                sidePanelState.status === "recipient-select" &&
                sidePanelState.configKey === field.key
              }
            >
              <Button
                size="small"
                color="secondary"
                variant="text"
                startIcon={<AddIcon />}
                sx={{ textWrap: "nowrap" }}
                onClick={() =>
                  setSidePanelState({
                    status: "recipient-select",
                    configKey: field.key,
                  })
                }
              >
                {t("Select recipient" as any, {
                  ns: "OrganisationSettings",
                })}
              </Button>
            </SelectedBoxWithIndicator>
          )}
        </Box>
      );
    }
    case "text":
      return <ConfigTextField configKey={field.key} label={field.label} />;
    case "number":
      return (
        <ConfigTextField
          configKey={field.key}
          label={field.label}
          type="number"
        />
      );
    case "boolean": {
      const checked = !!configValue;
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(_e, newChecked) => {
                setFieldValue("config", {
                  ...values.config,
                  [field.key]: newChecked,
                });
              }}
              size="small"
            />
          }
          label={t(field.label as any, { ns: "OrganisationSettings" })}
        />
      );
    }
    default:
      return <ConfigTextField configKey={field.key} label={field.label} />;
  }
}

function ConfigTextField({
  configKey,
  label,
  type,
}: {
  configKey: string;
  label: string;
  type?: string;
}) {
  const { t } = useTranslate(["OrganisationSettings"]);

  return (
    <TextField
      name={`config.${configKey}`}
      label={t(label as any, { ns: "OrganisationSettings" })}
      type={type}
    />
  );
}

// --- Helper Components ---

function MemberButtonLabel({ memberId }: { memberId: string }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const client = useApolloClient();

  const selectedQuery = useOrganisationMemberSelect_SelectedMembersQuery({
    client,
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      selectedMembers: [memberId],
      limit: 1,
    },
  });

  const selectedMember = selectedQuery.data?.selectedMembers.edges
    .map(e => e.node)
    .find(m => m.id === memberId);

  if (selectedMember) {
    return <>{selectedMember.fullname}</>;
  }

  if (selectedQuery.loading) {
    return "";
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <WarningIcon color="warning" sx={{ fontSize: 16 }} />
      <Typography variant="body2" color="warning.main">
        {t("Member no longer exists" as any, {
          ns: "OrganisationSettings",
        })}
      </Typography>
    </Stack>
  );
}

function WorkflowTypeWatcher({
  workflowTypeDefs,
}: {
  workflowTypeDefs: WorkflowTypeDefinitionsQuery["workflowTypeDefinitions"];
}) {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const prevRef = React.useRef(values.workflowType);

  React.useEffect(() => {
    if (values.workflowType && values.workflowType !== prevRef.current) {
      prevRef.current = values.workflowType;
      const typeDef = workflowTypeDefs.find(
        d => d.workflowType === values.workflowType
      );
      setFieldValue("sourceTypeFilter", []);
      setFieldValue("quoteTemplateFilter", []);
      setFieldValue("categoryFilter", []);
      setFieldValue("typeOfProjectFilter", []);
      const initialConfig: Record<string, unknown> = {};
      for (const field of typeDef?.configFields ?? []) {
        if (field.fieldType === "assignmentStrategyPicker") {
          initialConfig[field.key] = "specificUser";
        } else if (field.fieldType === "emailTemplatePicker") {
          initialConfig[field.key] = null;
        }
      }
      setFieldValue("config", initialConfig);
      setFieldValue(
        "triggerEventNames",
        typeDef?.compatibleTriggerEvents ?? []
      );
    }
  }, [values.workflowType, setFieldValue, workflowTypeDefs]);

  return null;
}

function SourceTypeCreatorWatcher({
  configFields,
}: {
  configFields: ConfigField[];
}) {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const assignStrategyField = configFields.find(
    f => f.fieldType === "assignmentStrategyPicker"
  );

  const assignTo = values.config.assignTo;
  const sourceTypeFilter = values.sourceTypeFilter;

  React.useEffect(() => {
    if (!assignStrategyField) return;
    if (assignTo !== "creator") return;

    const creatorCapableSourceTypes = ((
      assignStrategyField.metadata as Record<string, unknown> | null
    )?.creatorCapableSourceTypes ?? []) as string[];

    const hasSourceTypes = sourceTypeFilter.length > 0;
    const allCreatorCapable =
      hasSourceTypes &&
      sourceTypeFilter.every(st => creatorCapableSourceTypes.includes(st));

    if (!allCreatorCapable) {
      setFieldValue("config.assignTo", "specificUser");
      setFieldValue("config.recipientUserId", null);
    }
  }, [sourceTypeFilter, assignStrategyField, assignTo, setFieldValue]);

  return null;
}

// --- Side Panels ---

function EmailTemplateSidePanel({
  emailTemplates,
  handleClose,
  addEmailTemplate,
  configKey,
}: {
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  handleClose: () => void;
  addEmailTemplate: ReturnType<
    typeof useAddOrganisationEmailTemplateMutation
  >[0];
  configKey: string;
}) {
  const { t } = useTranslate(["OrganisationSettings", "Global"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { labels: emailTemplateContextLabels } = useEmailTemplateContexts();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const filteredTemplates = React.useMemo(() => {
    if (!searchTerm) return emailTemplates;
    const lower = searchTerm.toLowerCase();
    return emailTemplates.filter(
      tpl =>
        tpl.description.toLowerCase().includes(lower) ||
        (
          emailTemplateContextLabels[
            tpl.context as keyof typeof emailTemplateContextLabels
          ] ?? ""
        )
          .toLowerCase()
          .includes(lower)
    );
  }, [emailTemplates, searchTerm, emailTemplateContextLabels]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "description",
        headerName: t("Template", { ns: "OrganisationSettings" }),
        flex: 1,
        sortable: false,
      },
      {
        field: "context",
        headerName: t("Context", { ns: "OrganisationSettings" }),
        sortable: false,
        valueGetter: (value: string) =>
          emailTemplateContextLabels[
            value as keyof typeof emailTemplateContextLabels
          ] ?? value,
      },
      {
        field: "attachmentCount",
        flex: 0,
        maxWidth: 50,
        align: "right" as const,
        sortable: false,
        renderHeader: () => <AttachFileIcon />,
        headerAlign: "right" as const,
        valueGetter: (_value: unknown, row: (typeof emailTemplates)[0]) =>
          row.attachments.length,
      },
    ],
    [t, emailTemplateContextLabels]
  );

  const headerActions = (
    <ModalOpenButton
      Modal={OrganisationEmailTemplateModal}
      modalProps={{
        title: t("New email template", { ns: "OrganisationSettings" }),
        handleComplete: async (values: any, modalHandleClose: () => void) => {
          await addEmailTemplate({ variables: { input: values } });
          modalHandleClose();
        },
      }}
    >
      <ButtonCreate
        variant="contained"
        color="primary"
        title={t("Create new" as any, { ns: "OrganisationSettings" })}
      />
    </ModalOpenButton>
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select email template" as any, {
          ns: "OrganisationSettings",
        })}
        headerActions={headerActions}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={filteredTemplates}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("config", {
              ...values.config,
              [configKey]: selectedId,
            });
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}

function CheckboxFilterPanel({
  handleClose,
  title,
  items,
  selectedKeys,
  onToggle,
}: {
  handleClose: () => void;
  title: string;
  items: { key: string; label: React.ReactNode }[];
  selectedKeys: string[];
  onToggle: (key: string) => void;
}) {
  return (
    <>
      <ModalTitle handleClose={handleClose} title={title} />
      <ModalContent>
        <Stack spacing={0} overflow="auto">
          {items.map(item => (
            <FormControlLabel
              key={item.key}
              control={
                <Checkbox
                  checked={selectedKeys.includes(item.key)}
                  onChange={() => onToggle(item.key)}
                  size="small"
                />
              }
              label={item.label}
            />
          ))}
        </Stack>
      </ModalContent>
      <DialogActions>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  );
}

function SourceTypeSelectPanel({ handleClose }: { handleClose: () => void }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const items = SOURCE_TYPES.map(st => ({ key: st, label: st }));
  const handleToggle = (key: string) => {
    const current = values.sourceTypeFilter;
    setFieldValue(
      "sourceTypeFilter",
      current.includes(key) ? current.filter(s => s !== key) : [...current, key]
    );
  };
  return (
    <CheckboxFilterPanel
      handleClose={handleClose}
      title={t("Source types" as any, { ns: "OrganisationSettings" })}
      items={items}
      selectedKeys={values.sourceTypeFilter}
      onToggle={handleToggle}
    />
  );
}

function QuoteTemplateFilterPanel({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const client = useApolloClient();
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const { data, loading } = useTemplateQuoteSelectFieldQuery({
    client,
    variables: {
      limit: 500,
      filterApplicableFor: "QUOTE" as any,
    },
  });

  const templates = React.useMemo(() => {
    const connection = data?.quoteTemplates;
    if (!connection || connection.__typename !== "QuoteTemplateConnection")
      return [];
    return connection.edges.map(e => e.node);
  }, [data]);

  const orphanedIds = React.useMemo(() => {
    if (loading) return [];
    const existingIds = new Set(templates.map(tpl => tpl.id));
    return values.quoteTemplateFilter.filter(id => !existingIds.has(id));
  }, [loading, templates, values.quoteTemplateFilter]);

  const items: { key: string; label: React.ReactNode }[] = [
    ...templates.map(tpl => ({ key: tpl.id, label: tpl.title })),
    ...orphanedIds.map(id => ({
      key: id,
      label: (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <WarningIcon color="warning" sx={{ fontSize: 16 }} />
          <span>
            {t("Deleted template" as any, { ns: "OrganisationSettings" })}
          </span>
        </Stack>
      ),
    })),
  ];

  const handleToggle = (key: string) => {
    const current = values.quoteTemplateFilter;
    setFieldValue(
      "quoteTemplateFilter",
      current.includes(key) ? current.filter(k => k !== key) : [...current, key]
    );
  };

  if (loading) {
    return (
      <>
        <ModalTitle
          handleClose={handleClose}
          title={t("Quote templates" as any, { ns: "OrganisationSettings" })}
        />
        <ModalContent>
          <LoadingSpinner />
        </ModalContent>
      </>
    );
  }

  if (items.length === 0) {
    return (
      <>
        <ModalTitle
          handleClose={handleClose}
          title={t("Quote templates" as any, { ns: "OrganisationSettings" })}
        />
        <ModalContent>
          <Stack
            height="100%"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ p: 2, textAlign: "center" }}
          >
            <Typography variant="body1" color="text.secondary">
              {t(
                "No quote templates available yet. To use this filter, set up a quote template first." as any,
                { ns: "OrganisationSettings" }
              )}
            </Typography>
          </Stack>
        </ModalContent>
        <DialogActions>
          <IconButton color="primary" onClick={handleClose}>
            <KeyboardDoubleArrowLeftIcon />
          </IconButton>
        </DialogActions>
      </>
    );
  }

  return (
    <CheckboxFilterPanel
      handleClose={handleClose}
      title={t("Quote templates" as any, { ns: "OrganisationSettings" })}
      items={items}
      selectedKeys={values.quoteTemplateFilter}
      onToggle={handleToggle}
    />
  );
}

function CategoryFilterPanel({ handleClose }: { handleClose: () => void }) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Category filter" as any, { ns: "OrganisationSettings" })}
      />
      <ModalContent>
        <CategoriesInput
          categoryIds={values.categoryFilter}
          label={t("Category filter" as any, { ns: "OrganisationSettings" })}
          selectButtonLabel={t("Select categories" as any, {
            ns: "OrganisationSettings",
          })}
          onChange={async categories => {
            await setFieldValue(
              "categoryFilter",
              categories.map(c => c.id)
            );
          }}
          onClear={
            values.categoryFilter.length > 0
              ? async () => {
                  await setFieldValue("categoryFilter", []);
                }
              : undefined
          }
        />
      </ModalContent>
      <DialogActions>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
      </DialogActions>
    </>
  );
}

function TypeOfProjectFilterPanel({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { projectTypeOptions } = useProjectTypes();
  const items = projectTypeOptions.map(opt => ({
    key: opt.value,
    label: opt.label,
  }));
  const handleToggle = (key: string) => {
    const current = values.typeOfProjectFilter;
    setFieldValue(
      "typeOfProjectFilter",
      current.includes(key) ? current.filter(k => k !== key) : [...current, key]
    );
  };
  return (
    <CheckboxFilterPanel
      handleClose={handleClose}
      title={t("Type of project filter" as any, {
        ns: "OrganisationSettings",
      })}
      items={items}
      selectedKeys={values.typeOfProjectFilter}
      onToggle={handleToggle}
    />
  );
}

function TriggerEventSelectPanel({
  handleClose,
  availableEvents,
  compatibleEvents,
}: {
  handleClose: () => void;
  availableEvents: string[];
  compatibleEvents: string[];
}) {
  const { t } = useTranslate(["OrganisationSettings"]);
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const filteredEvents = React.useMemo(
    () => availableEvents.filter(e => compatibleEvents.includes(e)),
    [availableEvents, compatibleEvents]
  );

  const items = filteredEvents.map(e => ({ key: e, label: e }));
  const handleToggle = (key: string) => {
    const current = values.triggerEventNames;
    setFieldValue(
      "triggerEventNames",
      current.includes(key) ? current.filter(e => e !== key) : [...current, key]
    );
  };
  return (
    <CheckboxFilterPanel
      handleClose={handleClose}
      title={t("Trigger events" as any, { ns: "OrganisationSettings" })}
      items={items}
      selectedKeys={values.triggerEventNames}
      onToggle={handleToggle}
    />
  );
}

function RecipientSelectSidePanel({
  handleClose,
  configKey,
}: {
  handleClose: () => void;
  configKey: string;
}) {
  const { t } = useTranslate([
    "OrganisationSettings",
    "OrganisationProfile",
    "Global",
  ]);

  const { values, setFieldValue } = useFormikContext<FormValues>();
  const client = useApolloClient();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { getOrganisationRoleTitle } = useOrganisationRoles();

  const query = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting: [{ column: "firstname", direction: "asc" }],
      search: searchTerm || undefined,
    },
  });

  const members = React.useMemo(
    () =>
      (query.data ?? query.previousData)?.organisationMemberships.edges.map(
        edge => edge.node
      ) ?? [],
    [query.data, query.previousData]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "fullname",
        headerName: t("Name", { ns: "Global" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <UserAvatar userAvatar={row} size="s" />
            <Typography>{row.fullname}</Typography>
          </Stack>
        ),
      },
      {
        field: "roles",
        headerName: t("Roles", { ns: "OrganisationProfile" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) =>
          getOrganisationRoleTitle(row.roles),
      },
      {
        field: "email",
        headerName: t("Email", { ns: "Global" }),
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row: any) => row.email,
      },
    ],
    [t, getOrganisationRoleTitle]
  );

  return (
    <>
      <ModalTitle
        handleClose={handleClose}
        title={t("Select recipient" as any, {
          ns: "OrganisationSettings",
        })}
      />
      <ModalContent>
        <Stack height="100%" spacing={1}>
          <ListHeader
            SearchField={
              <DebouncedSearchInput
                placeholder={t("Search", { ns: "Global" })}
                defaultValue={searchTerm}
                onChangeSearchTerm={newValue => setSearchTerm(newValue)}
              />
            }
            marginBottom={0}
          />
          <Box flex={1}>
            <DataGrid
              columns={columns}
              rows={members}
              loading={query.loading}
              rowSelectionModel={{
                type: "include",
                ids: new Set(selectedId ? [selectedId] : []),
              }}
              onRowClick={({ row }) => {
                setSelectedId(selectedId === row.id ? null : row.id);
              }}
              hideFooter
              paginationMode="client"
              disableColumnMenu
              disableColumnResize
              disableRowSelectionOnClick
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        </Stack>
      </ModalContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <IconButton color="primary" onClick={handleClose}>
          <KeyboardDoubleArrowLeftIcon />
        </IconButton>
        <Button
          color="primary"
          variant="contained"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            setFieldValue("config", {
              ...values.config,
              [configKey]: selectedId,
            });
            handleClose();
          }}
        >
          {t("Select", { ns: "Global" })}
        </Button>
      </DialogActions>
    </>
  );
}
